export const paths = {
  home: '/',
  platform: '/platform',
  about: '/about',
  previousWork: '/previous-work',
  contact: '/contact',
  introductions: '/introductions',
  chat: '/chat',
  userSettings: '/user-settings',
  presentation: '/presentation',
  customDesigns: '/custom-designs',
  myAccount: '/app',
  /** Staff dashboard + ops */
  admin: '/admin',
  /** @deprecated Legacy path — redirects to paths.myAccount */
  myAccountLegacy: '/my-account',
  dashboard: '/dashboard',
  overview: '/overview',
  /** Primary app requests list + detail segment */
  requests: '/requests',
  /** Admin orders list + detail segment (under paths.admin) */
  adminOrders: '/orders',
  /** Admin requests overview (under paths.admin) */
  adminRequestsOverview: '/requests/overview',
  /** Landing-page guided assistant (explore mode) */
  guidedAssistant: '/guided-assistant',
  /** @deprecated Legacy path — redirects to paths.requests */
  orders: '/orders',
  settings: '/settings',
  checkout: '/checkout',
  campaign: '/c',

  // Role-specific landing pages
  forRole: '/for/:slug',
  forHR: '/for/hr',
  forMarketing: '/for/marketing',
  forOperations: '/for/operations',
  forEvents: '/for/events',
  forFounders: '/for/founders',

  allProducts: '/all-products',
  catalog: '/catalog',
  product: '/product',
  /** @deprecated Use paths.adminProducts — redirects to /admin/products */
  products: '/app/products',
  adminProducts: '/admin/products',
  linkProducts: '/app/link-products',
  /** @deprecated redirects to /admin/decorations */
  decorationsAdmin: '/app/decorations',
  adminDecorations: '/admin/decorations',
  /** @deprecated redirects to /admin/decorations-costs */
  decorationsCostsAdmin: '/app/decorations-costs',
  adminDecorationsCosts: '/admin/decorations-costs',
  /** @deprecated redirects to /admin/decorations-info */
  decorationsInfoAdmin: '/app/decorations-info',
  adminDecorationsInfo: '/admin/decorations-info',
  /** @deprecated redirects to /admin/decorations/new */
  decorationsNew: '/app/decorations/new',
  adminDecorationsNew: '/admin/decorations/new',
  /** @deprecated redirects to /admin/decorations/shared/new */
  decorationsSharedNew: '/app/decorations/shared/new',
  adminDecorationsSharedNew: '/admin/decorations/shared/new',
  /** @deprecated redirects to /admin/categories */
  categoriesAdmin: '/app/categories',
  adminCategories: '/admin/categories',
  organization: '/app/organization',
  /** @deprecated redirects to /admin/organizations */
  organizationsAdmin: '/app/organizations',
  adminOrganizations: '/admin/organizations',
  newClient: '/admin/organizations/new',
  /** @deprecated Use admin orders — redirects from /app/requests for staff */
  requestsAdmin: '/app/requests',
  /** @deprecated Legacy admin path — redirects to requestsAdmin */
  ordersAdmin: '/app/orders',
  /** @deprecated redirects to /admin/chats */
  chatsAdmin: '/app/chats',
  adminChats: '/admin/chats',
  adminInbox: '/admin/inbox',
  adminEmail: '/admin/email',
  adminInstructions: '/admin/instructions',
  adminAnalytics: '/admin/analytics',
  adminCompany: '/admin/company',

  cookieConsent: '/cookieConsent',
  privacyPolicy: '/privacy-policy',
  termsOfService: '/terms-of-service',
  /** @deprecated Legacy path — redirects to paths.privacyPolicy */
  privacyPolicyLegacy: '/privacyPolicy',
  /** @deprecated Legacy path — redirects to paths.termsOfService */
  termsOfServiceLegacy: '/termsOfService',
  loginSignUp: '/login-signup',
  login: '/login',
  signup: '/signup',
  signupSuccess: '/signup-success',
  earlyAccess: '/early-access',
  start: '/start',
  analysisSummary: '/analysis-summary',
  inventory: '/inventory',
  history: '/history',
  historyOrders: '/history/orders',
  historyItems: '/history/items',
  invoices: '/invoices',
  assets: '/assets',
  favourites: '/favourites',
  myAccountInventory: '/app/inventory',
  orderRequest: '/order-request',
  orderRequestSuccessful: '/order-request-successful',
  orderSuccessful: '/order-successful',
  invitation: '/invitation',
}

export const myAccountCatalogPath = `${paths.myAccount}${paths.catalog}`

export const myAccountRequestsPath = `${paths.myAccount}${paths.requests}`

export const adminOrdersPath = `${paths.admin}${paths.adminOrders}`

export const adminRequestsOverviewPath = `${paths.admin}${paths.adminRequestsOverview}`

export const isCatalogBrowsePath = (pathname: string) =>
  pathname === paths.allProducts || pathname === myAccountCatalogPath

export const buildGuidedAssistantPath = (publicId: string, view?: string) =>
  view
    ? `${paths.guidedAssistant}/${publicId}/${view}`
    : `${paths.guidedAssistant}/${publicId}`

export const isGuidedAssistantPath = (pathname: string) =>
  new RegExp(`^${paths.guidedAssistant}/[^/]+(?:/[^/]+)?$`).test(pathname)

export const isRequestDetailPath = (pathname: string) =>
  new RegExp(`^${paths.myAccount}${paths.requests}/[^/]+(?:/[^/]+)?$`).test(
    pathname
  )

export const isAdminOrderDetailPath = (pathname: string) =>
  new RegExp(`^${adminOrdersPath}/[^/]+(?:/[^/]+)?$`).test(pathname)

/** Customer request detail or admin order detail (full-bleed workspace) */
export const isOrderWorkspacePath = (pathname: string) =>
  isRequestDetailPath(pathname) || isAdminOrderDetailPath(pathname)

/** @deprecated Use isRequestDetailPath */
export const isOrderDetailPath = (pathname: string) =>
  isRequestDetailPath(pathname) ||
  new RegExp(`^${paths.myAccount}${paths.orders}/[^/]+$`).test(pathname)

export const buildAdminOrderPath = (publicId: string, view?: string) =>
  view
    ? `${adminOrdersPath}/${publicId}/${view}`
    : `${adminOrdersPath}/${publicId}`

export const buildAppRequestPath = (publicId: string, view?: string) =>
  view
    ? `${paths.myAccount}${paths.requests}/${publicId}/${view}`
    : `${paths.myAccount}${paths.requests}/${publicId}`

/** Internal path only — rejects protocol-relative and absolute URLs. */
export const parseNextPath = (raw: string | null | undefined): string | null => {
  if (!raw) return null
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('://')) {
    return null
  }
  return raw
}

export const buildLoginSignupPath = (
  next?: string | null,
  options?: { login?: boolean; oauth?: boolean }
) => {
  const params = new URLSearchParams()
  if (next) params.set('next', next)
  if (options?.login) params.set('login', '1')
  if (options?.oauth) params.set('oauth', '1')
  const query = params.toString()
  return query ? `${paths.loginSignUp}?${query}` : paths.loginSignUp
}
